<template>
  <main class="home-page">
    <section id="inicio" class="hero-section">
      <div class="hero-shell">
        <div class="hero-overlay"></div>
      <div class="site-container">
        <div class="hero-content">
          <p class="eyebrow">{{ content.hero.eyebrow }}</p>
            <h1>{{ content.hero.title }}</h1>
            <p class="hero-description">{{ content.hero.description }}</p>
            <div class="hero-actions">
              <a class="btn-primary" :href="content.hero.primaryCta.href">{{ content.hero.primaryCta.label }}</a>
              <router-link class="btn-secondary" :to="content.hero.secondaryCta.to">
                {{ content.hero.secondaryCta.label }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="stats-section">
      <div class="site-container stats-grid">
        <article v-for="metric in content.hero.metrics" :key="metric.label" class="stat-card">
          <strong>{{ metric.value }}</strong>
          <span>{{ metric.label }}</span>
        </article>
      </div>
    </section>

    <section id="servicios" class="section-shell">
      <div class="site-container">
        <div class="section-heading">
          <h2>Capacidades técnicas</h2>
          <p>Soluciones estructuradas para mitigar riesgos biológicos y asegurar el cumplimiento normativo.</p>
        </div>
        <div class="service-grid">
          <article v-for="service in content.services" :key="service.title" class="service-card">
            <p class="service-card__label">{{ service.title }}</p>
            <h3>{{ service.outcome }}</h3>
            <p>{{ service.details }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section-shell section-shell--muted">
      <div class="site-container proof-grid">
        <div class="proof-panel proof-panel--about">
          <div class="proof-panel__image-frame">
            <img src="/img/julio3.jpg" alt="Julio César Lamela" class="proof-panel__image" />
            <div class="proof-panel__data">
              <span>Dir. Principal</span>
            </div>
          </div>
        </div>
        <div id="credenciales" class="proof-panel proof-panel--copy">
          <h2>{{ content.about.name }}</h2>
          <p>{{ content.about.bio }}</p>
          <ul class="credential-list">
            <li v-for="credential in content.about.credentials" :key="credential">{{ credential }}</li>
          </ul>
          <div class="contact-links">
            <a
              v-for="contact in content.about.contacts"
              :key="contact.label"
              :href="contact.href"
              target="_blank"
              rel="noopener"
            >
              {{ contact.label }} / {{ contact.value }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="site-container systems-grid">
        <div class="systems-copy">
          <p class="eyebrow">Riesgos y metodología</p>
          <h2>Diagnóstico, prevención y control con criterio operacional.</h2>
        </div>
        <div class="systems-panel">
          <article class="systems-box">
            <h3>Dolores frecuentes</h3>
            <ul class="signal-list">
              <li v-for="item in content.painPoints" :key="item">{{ item }}</li>
            </ul>
          </article>
          <article class="systems-box">
            <h3>Método de trabajo</h3>
            <ol class="process-list">
              <li v-for="step in content.methodology" :key="step">{{ step }}</li>
            </ol>
          </article>
        </div>
      </div>
    </section>

    <section id="participaciones" class="section-shell section-shell--muted">
      <div class="site-container">
        <div class="section-heading section-heading--row">
          <div>
            <h2>Perspectivas técnicas</h2>
            <p>Participaciones, publicaciones y espacios donde el enfoque de JL Consultorías toma forma pública.</p>
          </div>
          <router-link to="/insights" class="section-link">Ver publicaciones</router-link>
        </div>
        <div class="participation-grid">
          <article v-for="participation in content.participations" :key="participation.title" class="participation-card">
            <img :src="participation.imageSrc" :alt="participation.title" />
            <div class="participation-card__body">
              <p class="participation-card__org">{{ participation.organization }}</p>
              <h3>{{ participation.title }}</h3>
              <p>{{ participation.description }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section-shell">
      <div class="site-container">
        <div class="section-heading section-heading--row">
          <div>
            <h2>Insights</h2>
            <p>Investigación aplicada y análisis sobre brotes, contaminación cruzada, limpieza y resistencia.</p>
          </div>
          <router-link to="/insights" class="section-link">Ver todos los artículos</router-link>
        </div>
        <div class="article-grid">
          <ArticleCard v-for="entry in featuredEntries" :key="entry.slug" :entry="entry" />
        </div>
      </div>
    </section>

    <section class="section-shell section-shell--muted">
      <div class="site-container faq-grid">
        <div class="faq-copy">
          <h2>Asegure sus operaciones</h2>
          <p>Minimice riesgos biológicos y alcance excelencia operativa con asesoría técnica especializada.</p>
        </div>
        <div class="faq-list">
          <article v-for="item in content.faq" :key="item.question" class="faq-item">
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="contacto" class="section-shell">
      <div class="site-container contact-shell">
        <div class="contact-copy">
          <p class="eyebrow">Contacto</p>
          <h2>{{ content.booking.title }}</h2>
          <p>{{ content.booking.description }}</p>
          <div class="hero-actions">
            <a class="btn-primary" :href="content.booking.calendlyUrl" target="_blank" rel="noopener">
              Iniciar evaluación técnica
            </a>
            <a class="btn-secondary" href="https://wa.me/59898300386" target="_blank" rel="noopener">
              WhatsApp
            </a>
          </div>
        </div>
        <div class="scheduler-shell">
          <vue-calendly :url="content.booking.calendlyUrl" height="920" />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import ArticleCard from '@/components/ArticleCard.vue';
import { siteContent } from '@/content/siteContent';
import { blogEntries } from '@/generated/blogEntries';
import { setSeoMeta } from '@/utils/seo';
import { normalizeEntry } from '@/utils/text';

export default {
  name: 'StarterPage',
  components: {
    ArticleCard
  },
  data() {
    return {
      content: siteContent
    };
  },
  computed: {
    featuredEntries() {
      return blogEntries.slice(0, 3).map(normalizeEntry);
    }
  },
  mounted() {
    this.applySeo();
  },
  methods: {
    applySeo() {
      setSeoMeta({
        title: 'JL Consultorías | Inocuidad alimentaria y control microbiológico',
        description:
          'Consultoría especializada en inocuidad alimentaria, auditorías microbiológicas, capacitación y diagnóstico para empresas de alimentos.',
        url: `${window.location.origin}/`,
        image: `${window.location.origin}/img/julio3.jpg`,
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: 'JL Consultorías',
          url: `${window.location.origin}/`,
          image: `${window.location.origin}/img/logo-jl.png`,
          telephone: '+59898300386',
          email: 'jlamela005@gmail.com',
          areaServed: 'Uruguay',
          founder: 'Julio César Lamela',
          sameAs: ['https://www.linkedin.com/in/julio-c%C3%A9sar-lamela-94743647/'],
          serviceType: [
            'Auditorías microbiológicas',
            'Capacitación técnica',
            'Asesoría en inocuidad alimentaria',
            'Diagnóstico de riesgo microbiológico'
          ]
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.hero-section {
  padding-top: 5rem;
  border-bottom: 1px solid rgba(59, 73, 76, 0.45);
}

.hero-shell {
  position: relative;
  min-height: 52rem;
  display: flex;
  align-items: center;
  padding-top: 4rem;
  padding-bottom: 4rem;
  width: 100%;
  background:
    linear-gradient(90deg, rgba(16, 20, 26, 0.96) 0%, rgba(16, 20, 26, 0.82) 42%, rgba(16, 20, 26, 0.18) 100%),
    url('/generated/blog/pilot-plant.jpg') center/cover no-repeat;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(10, 14, 20, 0.9) 0%, rgba(16, 20, 26, 0.8) 45%, rgba(16, 20, 26, 0.12) 100%);
}

.hero-content {
  position: relative;
  z-index: 1;
  width: min(100%, 42rem);
}

.hero-description {
  margin-bottom: 2rem;
  font-size: 1.125rem;
  color: var(--heading-color);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.stats-section {
  background: var(--bg-dim);
  border-bottom: 1px solid rgba(59, 73, 76, 0.45);
}

.stats-grid {
  display: grid;
  gap: 1.5rem;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.stat-card {
  padding-left: 1rem;
  border-left: 2px solid var(--accent-color);
}

.stat-card strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--accent-color);
  font-family: var(--font-display);
  font-size: 2rem;
}

.stat-card span {
  color: var(--heading-color);
  font-family: var(--font-label);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.section-shell--muted {
  background: var(--bg-elevated);
}

.section-heading {
  max-width: 44rem;
  margin-bottom: 2rem;
}

.section-heading p {
  color: var(--muted-color);
}

.section-heading--row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 1rem;
  max-width: none;
}

.section-link {
  align-self: end;
  color: var(--accent-color);
  font-family: var(--font-label);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.service-grid,
.article-grid,
.participation-grid {
  display: grid;
  gap: 1.5rem;
}

.service-card,
.faq-item,
.proof-panel,
.systems-box,
.participation-card,
.scheduler-shell {
  background: var(--surface-color);
  border: 1px solid rgba(59, 73, 76, 0.7);
}

.service-card {
  padding: 2rem;
}

.service-card__label,
.participation-card__org {
  color: var(--accent-color);
  font-family: var(--font-label);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.proof-grid,
.systems-grid,
.faq-grid,
.contact-shell {
  display: grid;
  gap: 2rem;
}

.proof-panel {
  height: 100%;
  padding: 2rem;
}

.proof-panel__image-frame {
  position: relative;
  height: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(59, 73, 76, 0.7);
  background: var(--bg-dim);
}

.proof-panel__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.proof-panel__data {
  position: absolute;
  right: 1.25rem;
  bottom: 1.25rem;
  display: grid;
  gap: 0.25rem;
  padding: 0.75rem;
  background: rgba(28, 32, 38, 0.92);
  border: 1px solid rgba(59, 73, 76, 0.7);
  color: var(--heading-color);
  font-family: var(--font-mono);
  font-size: 0.72rem;
  text-transform: uppercase;
}

.credential-list,
.signal-list,
.process-list {
  display: grid;
  gap: 0.8rem;
  padding-left: 1.25rem;
}

.contact-links {
  display: grid;
  gap: 0.6rem;
  margin-top: 1.5rem;
}

.contact-links a {
  color: var(--accent-color);
  font-family: var(--font-label);
  font-size: 0.74rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.systems-panel,
.faq-list {
  display: grid;
  gap: 1rem;
}

.systems-box,
.faq-item {
  padding: 1.5rem;
}

.participation-card img {
  display: block;
  width: 100%;
  height: 15rem;
  object-fit: cover;
}

.participation-card__body {
  padding: 1.5rem;
}

.contact-copy {
  max-width: 34rem;
}

.scheduler-shell {
  height: auto;
  overflow: visible;
}

.scheduler-shell :deep(.calendly-inline-widget) {
  min-width: 100%;
  min-height: 920px;
}

.scheduler-shell :deep(iframe) {
  min-height: 920px;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .service-grid,
  .article-grid,
  .participation-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .systems-panel,
  .faq-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 992px) {
  .proof-grid {
    grid-template-columns: minmax(18rem, 0.9fr) minmax(0, 1.1fr);
    align-items: stretch;
  }

  .systems-grid,
  .faq-grid,
  .contact-shell {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    align-items: start;
  }

  .proof-panel--copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

@media (max-width: 767px) {
  .hero-shell {
    min-height: 38rem;
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
}
</style>
